import type { Theme } from "@mui/material/styles";
import type { AppSx } from "../types";
import { buildFormLayoutStyles } from "../layout/formLayout";
import { FORM_CONTROL_MIN_HEIGHT } from "../tokens";

export interface AppFormFieldComponentStyles {
  innerStart: AppSx;
  innerEnd: AppSx;
  innerStretch: AppSx;
  visuallyHiddenInput: AppSx;
}

export function buildAppFormFieldComponentStyles(_theme: Theme): AppFormFieldComponentStyles {
  const base = {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  } as const;

  return {
    innerStart: { ...base, justifyContent: "flex-start" },
    innerEnd: { ...base, justifyContent: "flex-end" },
    innerStretch: { ...base, height: "100%", justifyContent: "flex-start" },
    visuallyHiddenInput: {
      clip: "rect(0 0 0 0)",
      clipPath: "inset(50%)",
      height: 1,
      overflow: "hidden",
      position: "absolute",
      bottom: 0,
      left: 0,
      whiteSpace: "nowrap",
      width: 1,
    },
  };
}

export interface AppRadioGroupComponentStyles {
  legend: AppSx;
}

export function buildAppRadioGroupComponentStyles(_theme: Theme): AppRadioGroupComponentStyles {
  return {
    legend: { fontSize: "0.875rem", fontWeight: 600, mb: 0.5 },
  };
}

export interface AppToggleFieldComponentStyles {
  label: AppSx;
}

export function buildAppToggleFieldComponentStyles(_theme: Theme): AppToggleFieldComponentStyles {
  return {
    label: {
      position: "relative",
      transform: "none",
      fontSize: "0.75rem",
      fontWeight: 600,
      mb: 0.5,
      color: "text.secondary",
    },
  };
}

export interface AppColorFieldComponentStyles {
  swatchButton: (color: string) => AppSx;
  popoverPaper: AppSx;
  popoverTitle: AppSx;
  swatchGrid: AppSx;
  swatchItem: (color: string) => AppSx;
  compactFormControl: AppSx;
  compactFormControlMerged: AppSx;
  compactInput: AppSx;
  compactTextField: AppSx;
  compactTextFieldMerged: AppSx;
  popoverContent: AppSx;
  helperText: AppSx;
}

export function buildAppColorFieldComponentStyles(theme: Theme): AppColorFieldComponentStyles {
  const form = buildFormLayoutStyles(theme);
  const whiteInset =
    "inset 0 0 0 1px rgba(0,0,0,0.12), inset 0 0 0 2px rgba(255,255,255,0.85)";

  return {
    swatchButton: (color) => ({
      width: 28,
      height: 28,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      bgcolor: color,
      flexShrink: 0,
      boxShadow: color.toLowerCase() === "#ffffff" ? whiteInset : undefined,
    }),
    popoverPaper: {
      bgcolor: "background.paper",
      backgroundImage: "none",
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1.5,
      mt: 0.5,
    },
    popoverTitle: { display: "block", mb: 1 },
    swatchGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      gap: 0.5,
    },
    swatchItem: (color) => ({
      width: 24,
      height: 24,
      borderRadius: 0.75,
      bgcolor: color,
      border: "1px solid",
      borderColor: "divider",
      boxShadow: color.toLowerCase() === "#ffffff" ? whiteInset : undefined,
    }),
    compactFormControl: { width: "100%", height: "100%" },
    compactFormControlMerged: { width: "100%", height: "100%", ...form.chartOptionInput },
    compactInput: { minHeight: FORM_CONTROL_MIN_HEIGHT },
    compactTextField: {
      flex: 1,
      minWidth: 0,
      "& .MuiInputBase-root": { minHeight: FORM_CONTROL_MIN_HEIGHT, height: FORM_CONTROL_MIN_HEIGHT },
      "& .MuiOutlinedInput-input, & .MuiInputBase-input": {
        color: "text.primary",
      },
      "& .MuiInputBase-input::placeholder": {
        color: "text.secondary",
        opacity: 1,
      },
    },
    compactTextFieldMerged: {
      flex: 1,
      minWidth: 0,
      ...form.appFormField,
      "& .MuiInputBase-root": { minHeight: FORM_CONTROL_MIN_HEIGHT, height: FORM_CONTROL_MIN_HEIGHT },
    },
    popoverContent: { p: 1.25, bgcolor: "background.paper" },
    helperText: { mx: 0, mt: 0.25 },
  };
}
